import React, { ReactElement, useCallback, useRef, useState } from 'react';
import ReactS3Uploader, { S3Response } from 'react-s3-uploader';
import ErrorMessage from '@app/common/ErrorMessage';
import Lang from '@lang/en';
import {
  StyledBtnContainer,
  StyledButton,
  StyledErrorCol,
  StyledInputContainer,
  StyledPercentage,
  StyledProgressBar,
  StyledProgressPerc,
  StyledUploadBtnWrapper,
} from './styles';
import usePresignedUrl from './usePresignedUrl';

export interface Props {
  orderId: number;
  onUploadFinishProp?: (url: string, previewUrl: string) => Promise<void>;
  callBack?: ({
    fileUploading,
    fileUploadError,
  }: {
    fileUploading?: boolean;
    fileUploadError?: string;
  }) => void;
  accept?: string;
  acl?: string;
  buttonText?: string;
  showError?: boolean;
  showProgress?: boolean;
  showLoadPercent?: boolean;
  CustomButton?: ReactElement;
  className?: string;
}

const FileUploadS3: React.FC<Props> = ({
  orderId,
  onUploadFinishProp,
  callBack,
  accept = '',
  acl = 'public-read',
  buttonText = Lang.browseText,
  showError = true,
  showProgress = true,
  showLoadPercent = true,
  CustomButton,
  className,
}) => {
  const initState = {
    loading: false,
    loadPercent: 0,
    error: '',
    filename: '',
    previewUrl: '',
  };
  const { getSignedUrl } = usePresignedUrl(orderId);
  const [state, setState] = useState<typeof initState>(initState);
  const uploaderRef = useRef<HTMLInputElement>();
  const persistentPreviewUrlRef = useRef<string>();

  const getSignedUrlHandler = (
    file: File,
    callbackWithUrl: (params: { signedUrl: string }) => void
  ): void => {
    getSignedUrl(file)
      .then((data) => {
        setState({
          ...state,
          filename: data.filename || '',
          previewUrl: data.previewUrl,
        });
        persistentPreviewUrlRef.current = data.previewUrl;
        callbackWithUrl(data);
      })
      .catch((error) => {
        setState({
          ...state,
          loading: false,
          error: error.message || Lang.preSignedUrlError,
        });
        callBack && callBack({ fileUploading: false });
      });
  };

  const onUploadProgress = (percent: number): void => {
    setState((state) => ({
      ...state,
      loading: true,
      loadPercent: percent || 1,
    }));
  };

  const onUploadError = (error: string): void => {
    setState({ ...state, loadPercent: 0, loading: false, error: error });
    callBack && callBack({ fileUploading: false, fileUploadError: error });
  };

  const onUploadFinish = useCallback((response: S3Response): void => {
    callBack &&
      callBack({
        fileUploading: false,
        fileUploadError: '',
      });
    onUploadFinishProp &&
      onUploadFinishProp(
        response.filename,
        persistentPreviewUrlRef.current || ''
      );
    setState((state) => ({
      ...state,
      loading: false,
      loadPercent: 0,
      error: '',
    }));
    // ReactS3Uploader won't respect updates of that function, so let's define it only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onButtonClick = useCallback(() => {
    if (uploaderRef.current) {
      uploaderRef.current.click();
    }
  }, [uploaderRef]);

  return (
    <StyledBtnContainer className={className}>
      <StyledUploadBtnWrapper onClick={onButtonClick}>
        {CustomButton || <StyledButton>{buttonText}</StyledButton>}
        <StyledInputContainer>
          <ReactS3Uploader
            getSignedUrl={getSignedUrlHandler}
            accept={accept}
            onProgress={onUploadProgress}
            onError={onUploadError}
            onFinish={onUploadFinish}
            uploadRequestHeaders={{}}
            contentDisposition="auto"
            inputRef={(ref) => {
              uploaderRef.current = ref;
            }}
            data-testid="healthcheck-add-results-input-file"
          />
        </StyledInputContainer>
      </StyledUploadBtnWrapper>
      {showError && state.error && (
        <StyledErrorCol>
          <ErrorMessage>{state.error}</ErrorMessage>
        </StyledErrorCol>
      )}
      {!!(showProgress && state.loadPercent && state.loadPercent > 0) && (
        <StyledProgressBar>
          <StyledProgressPerc style={{ width: `${state.loadPercent}%` }} />
        </StyledProgressBar>
      )}
      {!!(showLoadPercent && state.loadPercent && state.loadPercent > 0) && (
        <StyledPercentage>{state.loadPercent}%</StyledPercentage>
      )}
    </StyledBtnContainer>
  );
};

export default FileUploadS3;
